/**
 * State + thunks for the Org Settings → Configurations tab.
 *
 * Talks to /api/org/:orgId/configurations via orgConfigurationsAPI;
 * server is the authoritative gate (owner-only,
 * requirePermission('MANAGE_SUBSCRIPTION')).
 *
 * Same shape as CreatorSettingsConfigurationsPage.duck — save-all form
 * (typed strings), per-org context threaded via orgId on the thunks.
 */

import { orgConfigurationsAPI } from '../../api';
import { storableError } from '../../util/errors';

// ================ Action types ================ //

export const FETCH_CONFIG_REQUEST = 'app/OrgSettingsConfigurationsPage/FETCH_CONFIG_REQUEST';
export const FETCH_CONFIG_SUCCESS = 'app/OrgSettingsConfigurationsPage/FETCH_CONFIG_SUCCESS';
export const FETCH_CONFIG_ERROR = 'app/OrgSettingsConfigurationsPage/FETCH_CONFIG_ERROR';

export const SAVE_CONFIG_REQUEST = 'app/OrgSettingsConfigurationsPage/SAVE_CONFIG_REQUEST';
export const SAVE_CONFIG_SUCCESS = 'app/OrgSettingsConfigurationsPage/SAVE_CONFIG_SUCCESS';
export const SAVE_CONFIG_ERROR = 'app/OrgSettingsConfigurationsPage/SAVE_CONFIG_ERROR';

export const DOMAIN_REQUEST = 'app/OrgSettingsConfigurationsPage/DOMAIN_REQUEST';
export const DOMAIN_SUCCESS = 'app/OrgSettingsConfigurationsPage/DOMAIN_SUCCESS';
export const DOMAIN_ERROR = 'app/OrgSettingsConfigurationsPage/DOMAIN_ERROR';

// ================ Reducer ================ //

const initialState = {
  orgId: null,
  config: null,
  fetchInProgress: false,
  fetchLoaded: false,
  fetchError: null,
  saveInProgress: false,
  saveError: null,
  senderDomain: null,
  domainInProgress: false,
  domainError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_CONFIG_REQUEST:
      // On an org switch (different :orgId, same mounted component) drop the
      // previous org's config + sender domain so they can't flash under the
      // new org during its fetch. Same orgId → keep them (stale-while-revalidate).
      return payload.orgId !== state.orgId
        ? { ...initialState, orgId: payload.orgId, fetchInProgress: true }
        : { ...state, orgId: payload.orgId, fetchInProgress: true, fetchError: null };
    case FETCH_CONFIG_SUCCESS:
      return { ...state, fetchInProgress: false, fetchLoaded: true, config: payload.config };
    case FETCH_CONFIG_ERROR:
      return { ...state, fetchInProgress: false, fetchError: payload };

    case SAVE_CONFIG_REQUEST:
      return { ...state, saveInProgress: true, saveError: null };
    case SAVE_CONFIG_SUCCESS:
      return { ...state, saveInProgress: false, config: payload.config };
    case SAVE_CONFIG_ERROR:
      return { ...state, saveInProgress: false, saveError: payload };

    case DOMAIN_REQUEST:
      return { ...state, domainInProgress: true, domainError: null };
    case DOMAIN_SUCCESS:
      return { ...state, domainInProgress: false, senderDomain: payload.senderDomain };
    case DOMAIN_ERROR:
      return { ...state, domainInProgress: false, domainError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

const fetchConfigRequest = orgId => ({ type: FETCH_CONFIG_REQUEST, payload: { orgId } });
const fetchConfigSuccess = config => ({ type: FETCH_CONFIG_SUCCESS, payload: { config } });
const fetchConfigError = e => ({ type: FETCH_CONFIG_ERROR, payload: e });

const saveConfigRequest = () => ({ type: SAVE_CONFIG_REQUEST });
const saveConfigSuccess = config => ({ type: SAVE_CONFIG_SUCCESS, payload: { config } });
const saveConfigError = e => ({ type: SAVE_CONFIG_ERROR, payload: e });

// ================ Thunks ================ //

export const fetchOrgConfig = orgId => async dispatch => {
  dispatch(fetchConfigRequest(orgId));
  try {
    const response = await orgConfigurationsAPI.getConfig(orgId);
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(fetchConfigSuccess(data?.config || {}));
    return data?.config;
  } catch (e) {
    dispatch(fetchConfigError(storableError(e)));
    throw e;
  }
};

export const saveOrgConfig = (orgId, patch) => async dispatch => {
  dispatch(saveConfigRequest());
  try {
    const response = await orgConfigurationsAPI.updateConfig(orgId, patch);
    const body = response?.data ?? response ?? {};
    const data = body?.data ?? body;
    dispatch(saveConfigSuccess(data?.config));
    return data?.config;
  } catch (e) {
    dispatch(saveConfigError(storableError(e)));
    throw e;
  }
};

// ── Sender domain ──

const domainRequest = () => ({ type: DOMAIN_REQUEST });
const domainSuccess = senderDomain => ({ type: DOMAIN_SUCCESS, payload: { senderDomain } });
const domainError = e => ({ type: DOMAIN_ERROR, payload: e });

const pluckDomain = response => {
  const body = response?.data ?? response ?? {};
  const data = body?.data ?? body;
  return data?.senderDomain || null;
};

const domainThunk = apiCall => async dispatch => {
  dispatch(domainRequest());
  try {
    const senderDomain = pluckDomain(await apiCall());
    dispatch(domainSuccess(senderDomain));
    return senderDomain;
  } catch (e) {
    dispatch(domainError(storableError(e)));
    throw e;
  }
};

export const fetchOrgSenderDomain = orgId =>
  domainThunk(() => orgConfigurationsAPI.getSenderDomain(orgId));
export const connectOrgSenderDomain = (orgId, domain) =>
  domainThunk(() => orgConfigurationsAPI.connectSenderDomain(orgId, domain));
export const verifyOrgSenderDomain = orgId =>
  domainThunk(() => orgConfigurationsAPI.verifySenderDomain(orgId));
export const setOrgSenderFromEmail = (orgId, fromEmail) =>
  domainThunk(() => orgConfigurationsAPI.setSenderFromEmail(orgId, fromEmail));
export const disconnectOrgSenderDomain = orgId =>
  domainThunk(() => orgConfigurationsAPI.disconnectSenderDomain(orgId));

// ================ loadData ================ //

export const loadData = params => dispatch => {
  const { orgId } = params || {};
  if (!orgId) return Promise.resolve(null);
  return Promise.all([
    dispatch(fetchOrgConfig(orgId)),
    dispatch(fetchOrgSenderDomain(orgId)),
  ]);
};
